import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Gamepad2,
  Heart,
  Eye,
  ArrowRight,
  FileVideo,
  Clock,
  Upload,
} from "lucide-react";
import { API_BASE_URL } from '../../config';
import { cn } from '../../lib/utils';
import { getPlatformIcon } from '../../utils/platformIcons';

interface GameplayClip {
  id: string;
  filePath: string;
  thumbnailPath: string | null;
  mediaType: string;
  fileSize: number | null;
  aiMetadata: {
    videoTitle?: string;
    description?: string;
    tags?: string[];
  } | null;
  createdAt: string;
  publishInfo: {
    daysSincePublished: number;
    totalLikes: number;
    totalViews: number;
    platforms: string[];
  } | null;
}

interface GameplayContextResponse {
  clips: GameplayClip[];
}

interface GameplayMissionModalProps {
  isOpen: boolean;
  onClose: () => void;
  gameId: string;
  onSelectMedia: (media: any) => void;
  onStartFromScratch: () => void;
}

export const GameplayMissionModal: React.FC<GameplayMissionModalProps> = ({
  isOpen,
  onClose,
  gameId,
  onSelectMedia,
  onStartFromScratch,
}) => {
  const [state, setState] = useState<'loading' | 'results' | 'empty'>('loading');
  const [clips, setClips] = useState<GameplayClip[]>([]);
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!isOpen) return;

    setState('loading');
    setClips([]);

    const fetchContext = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/recommendations/gameplay-context`, {
          params: { gameId },
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = response.data as GameplayContextResponse;

        if (data.clips.length > 0) {
          setClips(data.clips);
          setState('results');
        } else {
          setState('empty');
        }
      } catch (error) {
        console.error('[GameplayMissionModal] Error fetching context:', error);
        setState('empty');
      }
    };

    fetchContext();
  }, [isOpen, gameId]);

  // Auto-redirect when no clips found
  useEffect(() => {
    if (state === 'empty') {
      const timer = setTimeout(() => {
        onStartFromScratch();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [state]);

  const getThumbnailUrl = (filePath: string, w: number = 300) => {
    return `${API_BASE_URL}/social/media/thumbnail?path=${encodeURIComponent(filePath)}&w=${w}&token=${token}`;
  };

  const formatNumber = (n: number) => {
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n.toString();
  };

  const getPublishLabel = (clip: GameplayClip) => {
    if (!clip.publishInfo) {
      return { text: 'Never shared', color: 'bg-green-500/10 text-green-400 border-green-500/20' };
    }
    const days = clip.publishInfo.daysSincePublished;
    if (days > 30) {
      return { text: `Shared ${days}d ago`, color: 'bg-amber-500/10 text-amber-400 border-amber-500/20' };
    }
    return { text: `Shared ${days}d ago`, color: 'bg-muted/50 text-muted-foreground border-border/50' };
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-lg bg-background border-border overflow-hidden">
        {/* Loading State */}
        {state === 'loading' && (
          <div className="py-12 text-center space-y-4">
            <div className="relative mx-auto w-16 h-16">
              <div className="absolute inset-0 rounded-full bg-primary/20 animate-ping" />
              <div className="relative flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 border border-primary/20">
                <Gamepad2 className="h-7 w-7 text-primary animate-pulse" />
              </div>
            </div>
            <div>
              <h3 className="text-base font-bold text-foreground">Finding your best clips...</h3>
              <p className="text-xs text-muted-foreground mt-1">Looking for gameplay videos to share</p>
            </div>
          </div>
        )}

        {/* Empty State */}
        {state === 'empty' && (
          <div className="py-12 text-center space-y-4">
            <div className="mx-auto w-16 h-16 flex items-center justify-center rounded-full bg-muted/50 border border-border">
              <FileVideo className="h-7 w-7 text-muted-foreground" />
            </div>
            <div>
              <h3 className="text-base font-bold text-foreground">No gameplay clips found</h3>
              <p className="text-xs text-muted-foreground mt-1">Opening create post...</p>
            </div>
          </div>
        )}

        {/* Results State */}
        {state === 'results' && (
          <>
            <DialogHeader>
              <DialogTitle className="text-sm font-black uppercase tracking-[0.15em] flex items-center gap-2">
                <div className="p-1.5 bg-primary/20 rounded-lg">
                  <Gamepad2 className="h-4 w-4 text-primary" />
                </div>
                Gameplay Clips
              </DialogTitle>
            </DialogHeader>

            <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider px-1">
              Pick a clip to share - prioritized by freshness
            </p>

            <div className="space-y-2 max-h-[350px] overflow-y-auto pr-1">
              {clips.map((clip, idx) => {
                const aiMeta = clip.aiMetadata;
                const title = aiMeta?.videoTitle || 'Untitled clip';
                const thumbPath = clip.thumbnailPath || clip.filePath;
                const publishLabel = getPublishLabel(clip);

                return (
                  <button
                    key={clip.id}
                    onClick={() => onSelectMedia(clip)}
                    className={cn(
                      "w-full flex items-center gap-3 p-2.5 rounded-xl border transition-all duration-200",
                      "bg-background/50 border-border/50 hover:border-primary/30 hover:bg-muted/60",
                      "text-left group/item cursor-pointer"
                    )}
                  >
                    {/* Thumbnail */}
                    <div className="relative shrink-0 w-16 h-16 rounded-lg overflow-hidden bg-muted border border-border/50">
                      {thumbPath && (
                        <img
                          src={getThumbnailUrl(thumbPath)}
                          alt={title}
                          className="w-full h-full object-cover"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                      )}
                      {idx === 0 && (
                        <div className="absolute top-0.5 left-0.5">
                          <Badge className="bg-primary/90 text-[8px] px-1 py-0 leading-tight">BEST</Badge>
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-semibold text-foreground truncate group-hover/item:text-primary transition-colors">
                        {title}
                      </h4>

                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        {/* Publish status badge */}
                        <Badge variant="outline" className={cn("text-[9px] px-1.5 py-0", publishLabel.color)}>
                          {clip.publishInfo ? <Clock className="h-2 w-2 mr-0.5" /> : <Upload className="h-2 w-2 mr-0.5" />}
                          {publishLabel.text}
                        </Badge>

                        {/* Stats if published */}
                        {clip.publishInfo && clip.publishInfo.totalLikes > 0 && (
                          <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                            <Heart className="h-2.5 w-2.5 text-red-400" />
                            {formatNumber(clip.publishInfo.totalLikes)}
                          </span>
                        )}
                        {clip.publishInfo && clip.publishInfo.totalViews > 0 && (
                          <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                            <Eye className="h-2.5 w-2.5 text-primary" />
                            {formatNumber(clip.publishInfo.totalViews)}
                          </span>
                        )}
                        {clip.publishInfo?.platforms.map(p => (
                          <span key={p} className="shrink-0">
                            {getPlatformIcon(p, 'h-2.5 w-2.5')}
                          </span>
                        ))}
                      </div>
                    </div>

                    <ArrowRight className="h-4 w-4 text-muted-foreground group-hover/item:text-primary shrink-0 transition-colors" />
                  </button>
                );
              })}
            </div>

            {/* Divider + Start from scratch */}
            <div className="relative py-2">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border/50" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-background px-3 text-[10px] text-muted-foreground uppercase tracking-widest">or</span>
              </div>
            </div>

            <Button
              variant="outline"
              className="w-full text-xs text-muted-foreground hover:text-foreground border-border/50"
              onClick={onStartFromScratch}
            >
              Start from scratch
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
